import { BlogCard } from './types';

export const ColicInBabiesBlogCards: BlogCard[] = [
  {
    title: {
      content:
        '### Colic in Babies: A Paediatrician’s Compassionate Guide for Exhausted Parents',
      styles: {
        h3: 'font-Mulish font-bold text-royal-875 my-3 text-base md:text-lg leading-tight line-clamp-2',
      },

      topicsStyles: {
        h3: 'text-theme-primary text-3xl md:text-4xl my-4 md:my-6 p-2 font-black ',
      },
    },
    description: {
      content:
        'Colic in babies causes prolonged crying and distress. A LIPS paediatrician explains the causes, warning signs, and when to see a doctor. Book at LIPS today.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    imageUrl: 'empaediatrics-blog.webp',
    bannerUrl: 'empaediatrics-blog.webp',
    slug: 'colic-in-babies',
    doctorSlug: 'emanuela-manea',
    contentType: 'article',
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'Why Do Babies Cry So Much? Understanding Normal Crying Patterns',
          id: 'why-do-babies-cry-so-much',
        },
        {
          text: 'What Is Colic in Babies? How Doctors Define It',
          id: 'what-is-colic-in-babies',
        },
        {
          text: 'What Causes Colic in Babies?',
          id: 'what-causes-colic-in-babies',
        },
        {
          text: 'How Colic in Babies Affects Parents and Families',
          id: 'how-colic-affects-parents-and-families',
        },
        {
          text: 'When Should You See a Paediatrician About Colic?',
          id: 'when-to-see-a-paediatrician-about-colic',
        },
        {
          text: 'How to Manage Colic in Babies at Home',
          id: 'how-to-manage-colic-in-babies-at-home',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    topics: {
      title: {
        content: '#### TOPICS:',
        styles: {
          h4: 'text-theme-primary font-extrabold p-2',
        },
      },
      topics: ['Colic in Babies', 'Paediatrics'],
      styles: {
        p: 'p-2 bg-info-500-opac-10 text-teal-450 bg-info-500-soft',
      },
    },
    speciality: 'Paediatrics',
    iconUrl: 'emanuela-manea.webp',
    date: {
      content: '27, July 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    postimonial: {
      content: 'BSc, MBChB, FRCS (Tr&Orth) \n Consultant Paediatrician',
      styles: {
        p: 'text-xl whitespace-pre-line',
      },
    },
    author: {
      content: 'Dr Emanuela Manea',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    content: {
      content: `
If you have a newborn who simply will not stop crying, this blog is for you. Colic in babies is one of the most common and distressing experiences of early parenthood, and yet it remains one of the least well understood.

I wrote this guide because I see the exhaustion, self-doubt and helplessness a colicky baby causes in families every week in my clinic. My aim is to explain what colic in babies actually is, what the research does and does not show, and to offer practical, reassuring guidance on colic relief and managing this difficult but usually temporary phase.


### Why Do Babies Cry So Much? Understanding Normal Crying Patterns

Crying is one of the earliest and most powerful forms of communication a baby has. Research tracking infant crying patterns across the first months of life consistently shows that healthy babies follow a relatively predictable pattern.

Baby crying tends to increase during the first weeks after birth, reach a peak somewhere between six and eight weeks of age, and gradually settle by around twelve weeks. Many parents also notice that colic in the evening is particularly common, with crying clustering in the late afternoon and early evening hours.

How long does colic last? For most babies, symptoms improve naturally by three to four months of age. In the first months of life, crying is part of your baby’s developmental adaptation to the outside world. It may communicate hunger, fatigue, overstimulation, discomfort, or simply the challenge of adjusting to life outside the womb. Although it can feel deeply distressing, colic in babies is almost always self-limiting and improves naturally with time.

<span id="what-is-colic-in-babies"></span>

### What Is Colic in Babies? How Doctors Define It

#### Wessel’s Rule of Threes

Historically, colic in babies was defined using Wessel’s criteria, often called the rule of threes. This classic definition describes colic as episodes of crying or fussing lasting at least three hours per day, on at least three days per week, for at least three weeks, in an otherwise healthy baby between two weeks and four months of age. It remains widely recognised today.

#### The Rome IV Criteria

More recently, the Rome IV criteria shifted the focus away from counting hours of crying and towards the caregiver’s experience of being unable to soothe the baby. Under this definition, colic in babies involves recurrent and prolonged crying or fussing in a baby under five months, with no obvious cause, that does not resolve despite reasonable soothing attempts, in a baby who is otherwise healthy and growing well.

During episodes, babies may show facial flushing, clenched fists, a tense abdomen and drawn-up legs. The inconsolable quality of the crying is one of the most distressing aspects for parents, many of whom feel that nothing they try makes any difference.

<span id="what-causes-colic-in-babies"></span>

### What Causes Colic in Babies?

Despite decades of research, there is no single proven cause of colic in babies. Current evidence suggests it reflects an interaction between several factors.

#### The Gut Microbiome

Studies have found that babies with colic may have lower levels of beneficial gut bacteria such as Bifidobacterium and Lactobacillus, alongside higher levels of inflammatory bacteria. This imbalance may contribute to gas production, intestinal inflammation and discomfort. Researchers describe this as the microbiota-gut-brain axis.

#### Gut and Digestive Immaturity

The first months of life involve rapid development of the digestive system. Immature bile acid production, an immature enteric nervous system (the network of nerves that controls the gut), and temporary lactase deficiency can all contribute to digestive discomfort, altered gut movement and increased gas.

#### Cow’s Milk Protein and Feeding Factors

Cow’s milk protein has been studied as a possible contributor to colic in babies. Some breastfed infants show improvement when mothers eliminate dairy from their diet, and some formula-fed infants improve with hydrolysed formula. However, results are inconsistent. If you are concerned about a possible cow’s milk protein allergy, this is something worth discussing at a paediatric assessment.

Breastfeeding technique can also play a role. One study of over 300 mother-infant pairs found that allowing one breast to empty fully before switching reduced the incidence of colic, possibly by improving the foremilk to hindmilk balance.

#### Reflux

Reflux symptoms frequently overlap with colic symptoms, which can cause understandable confusion. However, most studies do not support reflux as a primary cause of colic in babies, and anti-reflux medications generally show little benefit in reducing crying in this context.

#### Risk Factors

Several factors appear to increase the likelihood of colic in babies, including smoking during pregnancy, nicotine replacement therapy during pregnancy, and premature birth, particularly before 32 weeks.
<span id="how-colic-affects-parents-and-families"></span>

### How Colic in Babies Affects Parents and Families

Colic affects not only babies but entire families. Persistent inconsolable crying can lead to sleep deprivation, exhaustion, anxiety, frustration, feelings of helplessness and increased tension within relationships.

Parents commonly describe thoughts such as:

- “I am not a good parent.”
- “I cannot satisfy my baby.”
- “I feel helpless.”
- “I feel exhausted and overwhelmed.”

Some parents may also experience intrusive thoughts, anger, emotional burnout or moments of wanting to escape. These feelings can feel frightening and isolating, but they are far more common than many parents realise. They are understandable responses to prolonged stress, not signs of failure.

In my clinical practice, I assess parental wellbeing as part of every consultation for colic. I am interested in whether parents are sleeping or eating poorly, feeling emotionally overwhelmed, or struggling with confidence. Maternal anxiety during pregnancy and postpartum has also been associated with an increased likelihood of colic in some studies.

Colic is not caused by poor parenting. You did not cause this and you are not failing your baby.

Caring for a baby with colic can feel overwhelming and it is not something you should manage alone. Sharing the responsibility of soothing with a partner, family member or trusted friend, even for short periods, makes a real difference. If you are breastfeeding, expressing occasionally can allow another caregiver to take a feed and give you the rest you need. Accepting support when it is offered is not a sign of weakness. Looking after yourself is an essential part of looking after your baby.

<span id="when-to-see-a-paediatrician-about-colic"></span>

### When Should You See a Paediatrician About Colic?

Because crying can sometimes signal illness, one of my most important roles is to assess the baby carefully while supporting the family. The focus is not simply on the crying but on understanding the baby’s overall picture including feeding and sleeping patterns, weight gain and growth, development, and whether there are any signs of an underlying medical problem.

Although the vast majority of cases have no identifiable medical cause, the following symptoms warrant a prompt paediatric review:

- High-pitched or unusual crying that does not sound like your baby’s normal cry
- Fever or other signs of illness
- Vomiting or diarrhoea
- Poor weight gain
- Symptoms that persist beyond four months of age
- Loss of previously acquired skills or milestones
- Any finding on physical examination that concerns you or your doctor

If you have concerns about your baby’s crying, a <a target="_blank" rel="noopener noreferrer"><span style="color: orange">General Paediatric Consultation</span></a> can help clarify the picture, rule out any underlying cause, and provide structured guidance and reassurance.

<span id="how-to-manage-colic-in-babies-at-home"></span>

### How to Manage Colic in Babies at Home

#### The Baby Day Diary

One of the most helpful tools I recommend for families is a simple Baby Day Diary. Recording your baby’s crying episodes, feeding times, sleeping patterns, fussiness and responses to soothing over several days serves two important purposes.

First, it helps both you and your clinician identify patterns and assess whether a specific intervention is making a difference. Second, and equally importantly, it often provides genuine reassurance by showing that crying is following a predictable developmental pattern rather than becoming progressively worse.

#### Reassurance and Understanding

For many families, reassurance is one of the most effective interventions available. Understanding that colic is common, that crying is a normal part of infant communication and development, that symptoms almost always resolve by three to four months, and that you are not causing the crying can make a profound difference to how manageable the situation feels.

#### Practical Soothing Strategies

Management often focuses on creating calm, predictable routines aligned with your baby’s natural rhythms of feeding, sleeping and waking. Because every baby responds differently, I generally recommend trialling one intervention at a time for a reasonable period, typically one to two weeks, before deciding whether it is helping.

Strategies worth exploring include:

- Identifying what type of stimulation your baby prefers or finds overwhelming
- Establishing predictable sleep and feeding routines
- Skin-to-skin contact, gentle movement, white noise and sucking
- Ensuring parental rest and supporting your own mental wellbeing

#### Dietary Approaches

Diet is one of the most commonly explored areas when managing a colicky baby. Breastfeeding and colic are closely linked in some cases: some breastfed babies improve when their mother follows a dairy free colic diet, eliminating cow’s milk from her own diet. Formula and colic is equally worth considering for formula-fed infants, some of whom show improvement with a switch to a hydrolysed formula. Attention to breastfeeding technique also matters. Results vary considerably between babies and no single dietary approach works for everyone.

#### Probiotics

Probiotics for colic is one of the most common questions parents ask in clinic. Some studies suggest the probiotic *Lactobacillus reuteri* DSM 17938 may help reduce colic symptoms in some breastfed infants, and *Lactobacillus reuteri* colic research has grown significantly in recent years. However, a 2019 Cochrane systematic review concluded that there is no clear evidence that probiotics colic treatment is more effective than placebo in preventing colic in babies.

A note from my own clinical practice: babies presenting with persistent excessive crying are often already taking probiotics, in many cases consistently for weeks or months. By contrast, it is relatively uncommon to see families who have trialled probiotics for only two to three weeks before deciding whether they were helpful. If you are trying a probiotic, give it a consistent trial of at least two to three weeks before drawing conclusions.

#### Colic Remedies, Drops and Anti-Colic Bottles

There are a number of colic remedies parents commonly try at home. Herbal preparations such as fennel and chamomile, simethicone drops and lactase supplements all show variable effects between babies. Some parents report genuine colic relief, others notice little difference. These can reasonably be trialled for seven to ten days following package instructions. A trial of one to two days is generally insufficient to determine whether any benefit exists.

Anti-colic bottles are widely marketed as a way to reduce the amount of air a baby swallows during feeding. While there are no significant risks associated with them, the evidence that anti-colic bottles reduce colic in babies is limited. They may be worth trying as part of a broader approach, but they are unlikely to resolve the problem on their own. As with all colic remedies, I would encourage trialling one thing at a time so you can assess whether it is genuinely making a difference.
`,
      styles: {
        h3: 'text-lg md:text-[32px] font-bold text-theme-primary mb-4',
        h4: 'text-lg md:text-[20px] font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        em: 'not-italic font-bold',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
      },
    },
    faq: {
      heading: {
        content: '#### Frequently asked questions (FAQS)',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'How long does colic last?',
          },
          answer: {
            content:
              'Colic in babies typically improves naturally by three to four months of age. Most colicky babies show a gradual improvement from around twelve weeks. If symptoms persist beyond four to five months, a paediatric review is recommended to rule out any underlying cause.',
          },
        },
        {
          question: { content: 'When does colic start?' },
          answer: {
            content:
              'Colic in babies usually begins in the first two to three weeks of life and reaches its peak between six and eight weeks of age. It can appear in both breastfed and formula-fed babies.',
          },
        },
        {
          question: {
            content: 'What is the difference between colic and reflux?',
          },
          answer: {
            content:
              'Colic vs reflux is one of the most common questions I am asked. Both can cause significant distress and prolonged crying, and the two can overlap. Reflux typically involves bringing up milk, discomfort during or after feeds, and arching of the back. Colic tends to present as episodes of intense inconsolable crying without these feeding-related signs. A paediatric assessment can help distinguish between the two.',
          },
        },
        {
          question: {
            content: 'Can colic be caused by cow’s milk protein allergy?',
          },
          answer: {
            content: `In some babies, cow’s milk protein allergy can contribute to symptoms that overlap with colic. Signs that may suggest an allergy rather than straightforward colic include blood in stools, significant skin rashes, persistent vomiting or a family history of allergy. If you are concerned this may be a factor, it is worth discussing at a paediatric assessment.`,
          },
        },
        {
          question: {
            content: 'Do anti-colic bottles help?',
          },
          answer: {
            content:
              'Anti-colic bottles are designed to reduce the amount of air a baby swallows during feeding. While they are safe to use, the evidence that they reduce colic in babies is limited. They may be worth trying as part of a broader approach but are unlikely to resolve things on their own.',
          },
        },
        {
          question: {
            content: 'When should I see a paediatrician about my baby’s colic?',
          },
          answer: {
            content:
              'You should seek a paediatric review if your baby has a fever, poor weight gain, vomiting or diarrhoea, a high-pitched or unusual cry, or if symptoms persist beyond four months. You should also seek help if you feel overwhelmed or are struggling to cope. A private paediatric consultation in London can offer a timely, unhurried assessment and clear guidance tailored to your baby.',
          },
        },
      ],
    },
    postFaqContent: {
      content: `
### A Final Thought for Exhausted Parents


Colic in babies remains one of the most emotionally challenging experiences of early parenthood. Despite decades of research exploring gut bacteria, digestion, nervous system development and psychosocial factors, no single cause explains every baby.

The reassuring reality is that colic is almost always temporary. The most important things to hold on to are these:

- You are not failing.
- Your baby is not broken.
- This phase does pass.
- Support, reassurance and compassionate care matter enormously.

>If you are worried about your baby’s crying, or you would simply benefit from a calm, unhurried clinical assessment and some clear guidance, I am here to help. Appointments are available at clinics across London and online. You can find out more on the How Appointments Work page or request an appointment directly.

*"Sometimes, finding peace in the crying begins not with eliminating every tear, but with understanding that both babies and parents are learning, adapting, and growing together through one of the earliest storms of life."*

Consultant Paediatrician, Empaediatrics

---

>ⓘ Disclaimer: The content on this blog is written by Consultant Paediatrician, and is intended to provide general health information for parents and carers. It does not constitute medical advice and should not be used to diagnose, treat or manage any medical condition. Every child is different, and clinical findings and treatment decisions should always be made in the context of an individual assessment by a qualified healthcare professional. If you have concerns about your child’s health, please seek appropriate medical advice promptly. If your child is seriously unwell, call 999 or go to your nearest emergency department.
`,
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        h3: 'text-lg md:text-xl font-bold text-theme-primary mt-8 mb-4',
        a: 'text-text-action font-bold',
        em: 'italic',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 rounded-lg font-bold no-underline cursor-pointer mt-8 py-3 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        blockquote:
          'border-l-4 border-theme-primary bg-surface-secondary border border-blue-200 rounded-lg p-4 my-8 text-sm text-text-default not-italic',
      },
    },
  },
];
